/**
 * Deterministic production-crowd locomotion.
 * Visitors are not persisted; this module only samples poses from elapsed time.
 */

export type PathPoint = { x: number; z: number };

/** One 0.48-subcell step between the three paved lanes. */
export const PATROL_LANE_SPACING = 0.48;
export const PATROL_LANES = [-1, 0, 1] as const;
export type PatrolLane = (typeof PATROL_LANES)[number];

export interface PatrolWalkerSpec {
  offset: number;
  speed: number;
  lane: PatrolLane;
  pauseEvery: number;
  pauseFor: number;
  pausePhase: number;
}

export interface PatrolPose {
  x: number;
  z: number;
  facing: number;
  gait: number;
  paused: boolean;
  lane: PatrolLane;
  speed: number;
}

export function closedPathMetrics(points: PathPoint[]): { cumulative: number[]; total: number } {
  const cumulative = [0];
  let total = 0;
  for (let i = 0; i < points.length; i += 1) {
    const from = points[i]!;
    const to = points[(i + 1) % points.length]!;
    total += Math.hypot(to.x - from.x, to.z - from.z);
    cumulative.push(total);
  }
  return { cumulative, total };
}

export function poseOnClosedPath(points: PathPoint[], distance: number): { x: number; z: number; facing: number } {
  const info = closedPathMetrics(points);
  const wrapped = info.total <= 0 ? 0 : ((distance % info.total) + info.total) % info.total;
  let segment = 0;
  while (segment < info.cumulative.length - 2 && info.cumulative[segment + 1]! < wrapped) segment += 1;
  const from = points[segment]!;
  const to = points[(segment + 1) % points.length]!;
  const span = Math.max(info.cumulative[segment + 1]! - info.cumulative[segment]!, 0.0001);
  const local = (wrapped - info.cumulative[segment]!) / span;
  return {
    x: from.x + (to.x - from.x) * local,
    z: from.z + (to.z - from.z) * local,
    facing: Math.atan2(to.x - from.x, to.z - from.z),
  };
}

/**
 * Walk time in [0, elapsed] after a phase-shifted walk/pause cycle.
 * pausePhase only changes *when* a walker idles, not its starting distance.
 */
export function walkedTime(
  elapsed: number,
  pauseEvery: number,
  pauseFor: number,
  pausePhase: number,
): number {
  const walk = Math.max(pauseEvery, 0);
  const pause = Math.max(pauseFor, 0);
  const cycle = walk + pause;
  if (elapsed <= 0) {
    return 0;
  }
  if (cycle <= 0 || walk <= 0) {
    return elapsed;
  }

  const walkUpTo = (time: number): number => {
    if (time <= 0) {
      return 0;
    }
    const cycles = Math.floor(time / cycle);
    const rem = time - cycles * cycle;
    return cycles * walk + Math.min(rem, walk);
  };

  return walkUpTo(pausePhase + elapsed) - walkUpTo(pausePhase);
}

export function isPatrolPaused(
  elapsed: number,
  pauseEvery: number,
  pauseFor: number,
  pausePhase: number,
): boolean {
  const walk = Math.max(pauseEvery, 0);
  const pause = Math.max(pauseFor, 0);
  const cycle = walk + pause;
  if (cycle <= 0 || pause <= 0) {
    return false;
  }
  const phase = (((pausePhase + elapsed) % cycle) + cycle) % cycle;
  return phase >= walk;
}

export function applyLaneOffset(
  pose: { x: number; z: number; facing: number },
  lane: number,
  spacing = PATROL_LANE_SPACING,
): { x: number; z: number } {
  const offset = lane * spacing;
  return {
    x: pose.x + Math.cos(pose.facing) * offset,
    z: pose.z - Math.sin(pose.facing) * offset,
  };
}

/** Half a 0.48 subcell — the window on each side of a vertex used to blend lane normals. */
export const LANE_CORNER_BLEND_DISTANCE = 0.24;

function segmentFacing(from: PathPoint, to: PathPoint): number {
  return Math.atan2(to.x - from.x, to.z - from.z);
}

function nonzeroSegment(points: PathPoint[], cumulative: number[], start: number, dir: number): number {
  const count = points.length;
  let index = start;
  for (let step = 0; step < count; step += 1) {
    index = (index + dir + count) % count;
    if (cumulative[index + 1]! - cumulative[index]! > 1e-6) {
      return index;
    }
  }
  return start;
}

function offsetVector(facing: number, lane: number, spacing: number): { x: number; z: number } {
  const offset = lane * spacing;
  return { x: Math.cos(facing) * offset, z: -Math.sin(facing) * offset };
}

function lerpOffset(
  from: { x: number; z: number },
  to: { x: number; z: number },
  t: number,
): { x: number; z: number } {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  const smooth = clamped * clamped * (3 - 2 * clamped);
  return {
    x: from.x + (to.x - from.x) * smooth,
    z: from.z + (to.z - from.z) * smooth,
  };
}

/**
 * Instantaneous lane offset rotates with segment facing, so a 90° corner can
 * displace a ±1 walker by ~0.68 in one sample. Blend the incoming and outgoing
 * offset vectors across a half-cell window on each side of every vertex.
 */
export function applyLaneOffsetOnClosedPath(
  points: PathPoint[],
  distance: number,
  lane: number,
  spacing = PATROL_LANE_SPACING,
): { x: number; z: number } {
  const along = poseOnClosedPath(points, distance);
  if (lane === 0 || points.length < 2) {
    return { x: along.x, z: along.z };
  }
  const info = closedPathMetrics(points);
  if (info.total <= 0) {
    return { x: along.x, z: along.z };
  }
  const wrapped = ((distance % info.total) + info.total) % info.total;
  let segment = 0;
  while (segment < info.cumulative.length - 2 && info.cumulative[segment + 1]! < wrapped) {
    segment += 1;
  }
  const into = wrapped - info.cumulative[segment]!;
  const remain = info.cumulative[segment + 1]! - wrapped;
  const currentFacing = segmentFacing(points[segment]!, points[(segment + 1) % points.length]!);
  const prevIndex = nonzeroSegment(points, info.cumulative, segment, -1);
  const nextIndex = nonzeroSegment(points, info.cumulative, segment, 1);
  const prevFacing = segmentFacing(points[prevIndex]!, points[(prevIndex + 1) % points.length]!);
  const nextFacing = segmentFacing(points[nextIndex]!, points[(nextIndex + 1) % points.length]!);
  const blend = LANE_CORNER_BLEND_DISTANCE;
  const current = offsetVector(currentFacing, lane, spacing);
  let lateral = current;
  if (into <= blend && into <= remain) {
    const incoming = offsetVector(prevFacing, lane, spacing);
    lateral = lerpOffset(incoming, current, 0.5 + into / (2 * blend));
  } else if (remain <= blend) {
    const outgoing = offsetVector(nextFacing, lane, spacing);
    lateral = lerpOffset(current, outgoing, 0.5 * (1 - remain / blend));
  }
  return { x: along.x + lateral.x, z: along.z + lateral.z };
}

export function travelFacing(segmentFacing: number, speed: number): number {
  return speed < 0 ? segmentFacing + Math.PI : segmentFacing;
}

export function posePatrolWalker(points: PathPoint[], walker: PatrolWalkerSpec, elapsed: number): PatrolPose {
  const walked = walkedTime(elapsed, walker.pauseEvery, walker.pauseFor, walker.pausePhase);
  const total = closedPathMetrics(points).total;
  const distance = walker.offset * total + walked * walker.speed;
  const along = poseOnClosedPath(points, distance);
  const facing = travelFacing(along.facing, walker.speed);
  const offset = applyLaneOffsetOnClosedPath(points, distance, walker.lane);
  return {
    x: offset.x,
    z: offset.z,
    facing,
    gait: walked * Math.abs(walker.speed) * 9,
    paused: isPatrolPaused(elapsed, walker.pauseEvery, walker.pauseFor, walker.pausePhase),
    lane: walker.lane,
    speed: walker.speed,
  };
}
